import { useId, useLayoutEffect, useRef, useState } from "react";
import { fmtUsd, type FinancialsOverview } from "@/hooks/useFinancials";
import { halfWidthAt, icebergSlices, placeLabels, truncateToWidth } from "@shared/icebergLayout";
import { FIN_INK_CLASS } from "./chartColors";
import { icebergInputs } from "./icebergData";

const TIER = {
  upside: { title: "Opportunities", unit: "starred prospect", ink: FIN_INK_CLASS.upside },
  backlog: { title: "Active deals", unit: "engagement", ink: FIN_INK_CLASS.backlog },
} as const;

/**
 * Starred upside over active backlog, one slice per company. Both tiers are
 * projected money, so both are hatched (solid means collected on this tab);
 * slices alternate two tints of their tier's validated hue.
 */
export function PipelineIceberg({ data, includeUpside }: { data: FinancialsOverview; includeUpside: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const uid = useId().replace(/:/g, "");
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = icebergInputs(data, includeUpside);
  const empty = items.every((i) => i.amount <= 0);

  const r = typeof document !== "undefined" ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 15 : 15;
  const { w, h } = box;
  const tierW = Math.min(7.5 * r, w * 0.22);
  const labelW = Math.min(16 * r, w * 0.42);
  const padY = 0.9 * r;
  const top = padY;
  const height = Math.max(0, h - 2 * padY);
  const x0 = tierW + 0.8 * r;
  const minLabelSpace = Math.min(12 * r, w * 0.35);
  const triCap = w - 0.5 * r - minLabelSpace - 1.4 * r - x0;
  const triW = Math.max(40, Math.min(w - tierW - labelW - 2.4 * r, height * 0.95, triCap));
  const cx = x0 + triW / 2;
  const halfTop = triW / 2;
  const labelX = x0 + triW + 1.4 * r;
  const labelSpace = Math.max(0, w - labelX - 0.5 * r);
  const nameSize = 0.8125 * r;
  const subSize = 0.6875 * r;
  const family = (ref.current && typeof getComputedStyle !== "undefined" ? getComputedStyle(ref.current).fontFamily : "") || "sans-serif";
  let ctx: CanvasRenderingContext2D | null = null;
  try { ctx = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null; } catch { ctx = null; }
  const measureAt = (size: number, weight = 400) => (str: string) => {
    if (!ctx) return str.length * size * 0.55;
    ctx.font = `${weight} ${size}px ${family}`;
    return ctx.measureText(str).width;
  };
  const slices = icebergSlices(items, top, height);
  const labelY = placeLabels(slices.map((s) => (s.y0 + s.y1) / 2), 2.1 * r, top + 0.6 * r, top + height - 0.4 * r);
  const hw = (y: number) => halfWidthAt(y, top, height, halfTop);
  const tint = { upside: 0, backlog: 0 };

  const tiers = (["upside", "backlog"] as const).map((t) => {
    const mine = slices.filter((s) => s.tier === t);
    return mine.length ? { t, y0: mine[0].y0, y1: mine[mine.length - 1].y1, sum: mine.reduce((s, x) => s + x.amount, 0), n: mine.length } : null;
  }).filter(Boolean) as { t: "upside" | "backlog"; y0: number; y1: number; sum: number; n: number }[];

  return (
    <div ref={ref} className="relative h-full w-full min-h-[12rem]">
      {empty && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground text-center">No active deals or starred prospects with fees yet.</p>
      )}
      {!empty && (
        <ul className="sr-only">
          {tiers.map(({ t, sum, n }) => (
            <li key={`sr-${t}`}>{`${TIER[t].title}: ${fmtUsd(sum)}, ${n} ${TIER[t].unit}${n === 1 ? "" : "s"}`}</li>
          ))}
          {slices.map((s) => (
            <li key={`sr-${s.id}`}>{`${s.name}: ${fmtUsd(s.amount)}, ${s.sub}`}</li>
          ))}
        </ul>
      )}
      {!empty && w > 0 && h > 0 && (
        <svg width={w} height={h} className="absolute inset-0" aria-hidden="true">
          <defs>
            {(["upside", "backlog"] as const).flatMap((t) => [0, 1].map((k) => (
              <pattern key={`${t}${k}`} id={`${uid}-${t}-${k}`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" className={TIER[t].ink}>
                <rect width="5" height="5" fill="currentColor" fillOpacity={k ? 0.3 : 0.14} />
                <rect width="2" height="5" fill="currentColor" />
              </pattern>
            )))}
          </defs>
          {slices.map((s) => {
            const k = tint[s.tier]++ % 2;
            const pts = `${cx - hw(s.y0)},${s.y0} ${cx + hw(s.y0)},${s.y0} ${cx + hw(s.y1)},${s.y1} ${cx - hw(s.y1)},${s.y1}`;
            return (
              <polygon key={s.id} points={pts} fill={`url(#${uid}-${s.tier}-${k})`} className="stroke-card" strokeWidth={2}>
                <title>{`${s.name}: ${fmtUsd(s.amount)} (${s.sub})`}</title>
              </polygon>
            );
          })}
          {slices.map((s, i) => {
            const mid = (s.y0 + s.y1) / 2;
            const ax = cx + hw(mid);
            const ly = labelY[i];
            const amt = fmtUsd(s.amount);
            const prefix = s.tier === "upside" ? "★ " : "";
            const nameRoom = labelSpace - measureAt(nameSize, 700)(` ${amt}`) - measureAt(nameSize)(prefix);
            const shownName = truncateToWidth(s.name, nameRoom, measureAt(nameSize));
            const shownSub = truncateToWidth(s.sub, labelSpace, measureAt(subSize));
            return (
              <g key={`l-${s.id}`}>
                <title>{s.name}</title>
                <polyline points={`${ax + 2},${mid} ${ax + 0.6 * r},${mid} ${labelX - 0.4 * r},${ly}`} fill="none" className="stroke-muted-foreground/50" strokeWidth={1} />
                <circle cx={ax + 2} cy={mid} r={1.8} className="fill-muted-foreground" />
                <text x={labelX} y={ly - 0.1 * r} fontSize={nameSize} className="fill-foreground">
                  {prefix}{shownName} <tspan fontWeight={700}>{amt}</tspan>
                </text>
                <text x={labelX} y={ly + 0.8 * r} fontSize={subSize} className="fill-muted-foreground">{shownSub}</text>
              </g>
            );
          })}
          {tiers.map(({ t, y0, y1, sum, n }) => {
            const bx = tierW;
            const mid = (y0 + y1) / 2;
            return (
              <g key={`t-${t}`} className={TIER[t].ink}>
                <path d={`M${bx + 0.4 * r},${y0 + 2} H${bx} V${y1 - 2} H${bx + 0.4 * r}`} fill="none" stroke="currentColor" strokeWidth={1.5} />
                <text x={bx - 0.5 * r} y={mid - 0.6 * r} textAnchor="end" fontSize={0.6875 * r} className="fill-muted-foreground">{TIER[t].title}</text>
                <text x={bx - 0.5 * r} y={mid + 0.75 * r} textAnchor="end" fontSize={1.2 * r} fontWeight={700} className="fill-foreground">{fmtUsd(sum)}</text>
                <text x={bx - 0.5 * r} y={mid + 1.8 * r} textAnchor="end" fontSize={0.625 * r} className="fill-muted-foreground">{n} {TIER[t].unit}{n === 1 ? "" : "s"}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
